import './App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import axios from 'axios';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

import { UserContextProvider } from './context/userContext/userContext';
import { StyleProvider } from './context/styleContext/styleContext';
import { ShoppingCartProvider } from './context/shoppingCartContext/shoppingCartContext';
import { PromotionsProvider } from './context/promotionsContext/promotionsContext';

import PrivateRoutes from './routes/PrivateRoutes';
import AdminRoutes from './routes/AdminRoutes';
import SiteLayout from './components/SiteLayout/SiteLayout';
import CookieHandler from './components/CookieHandler/CookieHandler';

// Public pages
const Home = lazy(() => import('./pages/Home/Home'));
const Register = lazy(() => import('./pages/Register/Register'));
const Login = lazy(() => import('./pages/Login/Login'));
const About = lazy(() => import('./pages/About/About'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const FAQ = lazy(() => import('./pages/FAQ/FAQ'));
const Support = lazy(() => import('./pages/Support/Support'));
const Terms = lazy(() => import('./pages/Terms/Terms'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy/ReturnPolicy'));
const Plugins = lazy(() => import('./pages/Plugins/Plugins'));
const ProductsPage = lazy(() => import('./pages/Products/ProductsPage'));
const ProductPage = lazy(() => import('./pages/Product/ProductPage'));
const ProductReviewPage = lazy(() => import('./pages/Product/ProductReviewPage'));
const AllReleases = lazy(() => import('./pages/AllReleases/AllReleases'));
const ShoppingCart = lazy(() => import('./pages/ShoppingCart/ShoppingCart'));
const BundlesPage = lazy(() => import('./pages/Bundles/BundlesPage'));
const BundlePage = lazy(() => import('./pages/Bundle/BundlePage'));
const RedeemPage = lazy(() => import('./pages/Redeem/Redeem'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword/ResetPassword'));
const UnsubscribeToMailingList = lazy(() =>
  import('./pages/UnsubscribeToMailingList/UnsubscribeToMailingList')
);
const Subscribe = lazy(() => import('./pages/Subscribe/Subscribe'));

// Private customer pages
const Account = lazy(() => import('./pages/Account/Account'));
const MyGear = lazy(() => import('./pages/myGear/MyGear'));
const CheckoutPage = lazy(() => import('./pages/Checkout/CheckoutPage'));
const ManageSubscription = lazy(() =>
  import('./pages/Subscription/ManageSubscription')
);
const SubConfirmPage = lazy(() =>
  import('./pages/Subscription/SubConfirmPage')
);
const CheckoutConfirmationPage = lazy(() =>
  import('./pages/CheckoutConfirmation/CheckoutConfirmation')
);
const CheckoutProcessingPage = lazy(() =>
  import('./pages/CheckoutProcessing/CheckoutProcessing')
);

// Admin pages
const Admin = lazy(() => import('./pages/Admin/Admin'));
const CreateLicense = lazy(() =>
  import('./components/CreateLicense/CreateLicense')
);
const GainDemo = lazy(() => import('./pages/GainDemo/GainDemo'));
const ImageRendering = lazy(() => import('./pages/Admin/ImageRendering'));
const AdminPromotionsPage = lazy(() =>
  import('./pages/Admin/AdminPromotions')
);
const AdminProductsPage = lazy(() =>
  import('./pages/Admin/AdminProductsPage')
);
const AdminProductPage = lazy(() =>
  import('./pages/Admin/AdminProductPage')
);
const EmailTesting = lazy(() => import('./pages/Admin/EmailTesting'));
const MarketingEmailTesting = lazy(() =>
  import('./pages/Admin/MarketingEmail')
);
const PressEmailPage = lazy(() => import('./pages/Admin/PressEmailPage'));
const AdminPluginUsagePage = lazy(() =>
  import('./pages/Admin/AdminPluginUsagePage')
);
const AdminContactsPage = lazy(() =>
  import('./pages/Admin/AdminContactPage')
);
const AdminRedemptionCodes = lazy(() =>
  import('./pages/Admin/RedemptionCodes')
);
const AdminBundlesPage = lazy(() =>
  import('./pages/Admin/AdminBundlesPage')
);

// Learning Center pages
const Learning = lazy(() => import('./pages/Learning/Learning'));
const AutomationInMixingLearning = lazy(() =>
  import('./pages/Learning/AutomationInMixing')
);
const BitcrusherLearning = lazy(() => import('./pages/Learning/Bitcrusher'));
const BusProcessingLearning = lazy(() =>
  import('./pages/Learning/BusProcessing')
);
const ChorusLearning = lazy(() => import('./pages/Learning/Chorus'));
const ClippingVsLimitingLearning = lazy(() =>
  import('./pages/Learning/ClippingVsLimiting')
);
const CompareDistOdFuzz = lazy(() =>
  import('./pages/Learning/CompareDistOdFuzz')
);
const CompressionLearning = lazy(() =>
  import('./pages/Learning/Compression')
);
const DelayLearning = lazy(() => import('./pages/Learning/Delay'));
const DistortionLearning = lazy(() => import('./pages/Learning/Distortion'));
const DynamicEQLearning = lazy(() => import('./pages/Learning/DynamicEQ'));
const EQLearning = lazy(() => import('./pages/Learning/EQ'));
const HarmonicSaturationLearning = lazy(() =>
  import('./pages/Learning/HarmonicSaturation')
);
const SaturationVsDistortionVsClippingLearning = lazy(() =>
  import('./pages/Learning/SaturationVsDistortionVsClipping')
);
const WhatIsATransientLearning = lazy(() =>
  import('./pages/Learning/WhatIsATransient')
);
const MakeVocalsSoundFullerLearning = lazy(() =>
  import('./pages/Learning/HowToMakeVocalsSoundFuller')
);
const SoftClippingVsHardClippingLearning = lazy(() =>
  import('./pages/Learning/SoftClippingVsHardClipping')
);
const LimitingLearning = lazy(() => import('./pages/Learning/Limiting'));
const MultibandCompressionLearning = lazy(() =>
  import('./pages/Learning/MultibandCompression')
);
const OverdriveLearning = lazy(() => import('./pages/Learning/Overdrive'));
const ParallelProcessingLearning = lazy(() =>
  import('./pages/Learning/ParallellProcessing')
);
const ReverbLearning = lazy(() => import('./pages/Learning/Reverb'));
const SaturationLearning = lazy(() => import('./pages/Learning/Saturation'));
const NoiseGateLearning = lazy(() => import('./pages/Learning/Noisegate'));
const DeEsserLearning = lazy(() => import('./pages/Learning/Deesser'));
const FlangerLearning = lazy(() => import('./pages/Learning/Flanger'));
const GainStagingLearning = lazy(() =>
  import('./pages/Learning/GainStaging')
);
const StereoImagingLearning = lazy(() =>
  import('./pages/Learning/StereoImaging')
);
const SidechainCompressionLearning = lazy(() =>
  import('./pages/Learning/SidechainCompression')
);
const TremoloLearning = lazy(() => import('./pages/Learning/Tremolo'));
const VibratoLearning = lazy(() => import('./pages/Learning/Vibrato'));
const PhaserLearning = lazy(() => import('./pages/Learning/Phaser'));
const TransientShaperLearning = lazy(() =>
  import('./pages/Learning/TransientShaper')
);
const CompressorVsLimiterLearning = lazy(() =>
  import('./pages/Learning/CompressorVsLimiter')
);
const MasteringBasicsLearning = lazy(() =>
  import('./pages/Learning/MasteringBasics')
);
const FixMuddyMixLearning = lazy(() =>
  import('./pages/Learning/FixMuddyMix')
);
const VocalsSitInMixLearning = lazy(() =>
  import('./pages/Learning/MakeVocalsSitInMix')
);
const ConsistentAudioLevelsLearning = lazy(() =>
  import('./pages/Learning/KeepLevelsConsistent')
);
const PunchyDrumsLearning = lazy(() =>
  import('./pages/Learning/GetPunchyDrums')
);
const TightenLowEndLearning = lazy(() =>
  import('./pages/Learning/TightenLowEnd')
);
const LouderMixWithoutClippingLearning = lazy(() =>
  import('./pages/Learning/MixLouderWithoutClipping')
);
const VocalProcessingChainLearning = lazy(() =>
  import('./pages/Learning/VocalProcessingChain')
);
const BestReverbSettingsForVocalsLearning = lazy(() =>
  import('./pages/Learning/ReverbSettingsForVocals')
);

if (process.env.NODE_ENV === 'development') {
  axios.defaults.baseURL = 'http://localhost:8080';
  // axios.defaults.baseURL = 'https://api.parishaudio.com/';
} else {
  axios.defaults.baseURL = 'https://api.parishaudio.com/';
}

axios.defaults.withCredentials = true;

function PageLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '40vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      Loading...
    </div>
  );
}

function RouteSuspense() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <UserContextProvider>
          <StyleProvider>
            <PromotionsProvider>
              <ShoppingCartProvider>
                <CookieHandler />

                <Toaster
                  position="bottom-right"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      borderRadius: '8px',
                      background: '#1f2937',
                      color: '#f9fafb',
                      padding: '12px 16px',
                      fontSize: '1.3rem',
                    },
                    success: {
                      iconTheme: {
                        primary: '#10b981',
                        secondary: '#d1fae5',
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fee2e2',
                      },
                    },
                  }}
                />

                <Routes>
                  <Route element={<SiteLayout />}>
                    <Route element={<RouteSuspense />}>
                      <Route element={<PrivateRoutes />}>
                        <Route path="account" element={<Account />} />
                        <Route path="/my-gear" element={<MyGear />} />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route
                          path="/manage-subscriptions"
                          element={<ManageSubscription />}
                        />
                        <Route
                          path="/subscription-confirmation"
                          element={<SubConfirmPage />}
                        />
                        <Route
                          path="/checkout-confirmation"
                          element={<CheckoutConfirmationPage />}
                        />
                        <Route
                          path="/checkout-processing"
                          element={<CheckoutProcessingPage />}
                        />
                      </Route>





                      <Route element={<AdminRoutes />}>
                        <Route path="/admin" element={<Admin />} />
                        <Route
                          path="/admin/create-license"
                          element={<CreateLicense />}
                        />
                        <Route path="/gain-demo" element={<GainDemo />} />
                        <Route
                          path="/admin/image-rendering"
                          element={<ImageRendering />}
                        />
                        <Route
                          path="admin/promotions"
                          element={<AdminPromotionsPage />}
                        />
                        <Route
                          path="/admin/products"
                          element={<AdminProductsPage />}
                        />
                        <Route
                          path="/admin/products/:productId"
                          element={<AdminProductPage />}
                        />
                        <Route
                          path="/admin/email-testing"
                          element={<EmailTesting />}
                        />
                        <Route
                          path="/admin/marketing-email"
                          element={<MarketingEmailTesting />}
                        />
                        <Route
                          path="/admin/press-email"
                          element={<PressEmailPage />}
                        />
                        <Route
                          path="/admin/plugin-usage"
                          element={<AdminPluginUsagePage />}
                        />
                        <Route
                          path="/admin/contacts"
                          element={<AdminContactsPage />}
                        />
                        <Route
                          path="/admin/redemption-codes"
                          element={<AdminRedemptionCodes />}
                        />
                        <Route
                          path="/admin/bundles"
                          element={<AdminBundlesPage />}
                        />
                      </Route>









                      

                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/all-releases" element={<AllReleases />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route path="/support" element={<Support />} />
                      <Route path="/return-policy" element={<ReturnPolicy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/plugins" element={<Plugins />} />
                      <Route path="/products" element={<ProductsPage />} />
                      <Route path="/bundles" element={<BundlesPage />} />
                      <Route
                        path="/unsubscribe-to-mailing-list"
                        element={<UnsubscribeToMailingList />}
                      />
                      <Route path="/register" element={<Register />} />
                      <Route
                        path="/shopping-cart"
                        element={<ShoppingCart />}
                      />
                      <Route path="/redeem" element={<RedeemPage />} />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="/reset-password/:token"
                        element={<ResetPassword />}
                      />

                      <Route path="/learning" element={<Learning />} />
                      <Route
                        path="/learning/automation-in-mixing"
                        element={<AutomationInMixingLearning />}
                      />
                      <Route
                        path="/learning/bitcrusher"
                        element={<BitcrusherLearning />}
                      />
                      <Route
                        path="/learning/bus-processing"
                        element={<BusProcessingLearning />}
                      />
                      <Route
                        path="/learning/chorus"
                        element={<ChorusLearning />}
                      />
                      <Route
                        path="/learning/clipping-vs-limiting"
                        element={<ClippingVsLimitingLearning />}
                      />
                      <Route
                        path="/learning/compare-distortion-overdrive-fuzz"
                        element={<CompareDistOdFuzz />}
                      />
                      <Route
                        path="/learning/compression"
                        element={<CompressionLearning />}
                      />
                      <Route
                        path="/learning/delay"
                        element={<DelayLearning />}
                      />
                      <Route
                        path="/learning/distortion"
                        element={<DistortionLearning />}
                      />
                      <Route
                        path="/learning/dynamic-eq"
                        element={<DynamicEQLearning />}
                      />
                      <Route
                        path="/learning/equalization"
                        element={<EQLearning />}
                      />
                      <Route
                        path="/learning/harmonic-saturation"
                        element={<HarmonicSaturationLearning />}
                      />
                      <Route
                        path="/learning/saturation-vs-distortion-vs-clipping"
                        element={<SaturationVsDistortionVsClippingLearning />}
                      />
                      <Route
                        path="/learning/what-is-a-transient"
                        element={<WhatIsATransientLearning />}
                      />
                      <Route
                        path="/learning/how-to-make-vocals-sound-fuller"
                        element={<MakeVocalsSoundFullerLearning />}
                      />
                      <Route
                        path="/learning/soft-clipping-vs-hard-clipping"
                        element={<SoftClippingVsHardClippingLearning />}
                      />
                      <Route
                        path="/learning/limiting"
                        element={<LimitingLearning />}
                      />
                      <Route
                        path="/learning/multi-band-compression"
                        element={<MultibandCompressionLearning />}
                      />
                      <Route
                        path="/learning/overdrive"
                        element={<OverdriveLearning />}
                      />
                      <Route
                        path="/learning/parallel-processing"
                        element={<ParallelProcessingLearning />}
                      />
                      <Route
                        path="/learning/reverb"
                        element={<ReverbLearning />}
                      />
                      <Route
                        path="/learning/saturation"
                        element={<SaturationLearning />}
                      />
                      <Route
                        path="/learning/noise-gate"
                        element={<NoiseGateLearning />}
                      />
                      <Route
                        path="/learning/de-esser"
                        element={<DeEsserLearning />}
                      />
                      <Route
                        path="/learning/flanger"
                        element={<FlangerLearning />}
                      />
                      <Route
                        path="/learning/gain-staging"
                        element={<GainStagingLearning />}
                      />
                      <Route
                        path="/learning/stereo-imaging"
                        element={<StereoImagingLearning />}
                      />
                      <Route
                        path="/learning/sidechain-compression"
                        element={<SidechainCompressionLearning />}
                      />
                      <Route
                        path="/learning/tremolo"
                        element={<TremoloLearning />}
                      />
                      <Route
                        path="/learning/vibrato"
                        element={<VibratoLearning />}
                      />
                      <Route
                        path="/learning/phaser"
                        element={<PhaserLearning />}
                      />
                      <Route
                        path="/learning/transient-shaper"
                        element={<TransientShaperLearning />}
                      />
                      <Route
                        path="/learning/compressor-vs-limiter"
                        element={<CompressorVsLimiterLearning />}
                      />
                      <Route
                        path="/learning/mastering-basics"
                        element={<MasteringBasicsLearning />}
                      />
                      <Route
                        path="/learning/fix-muddy-mix"
                        element={<FixMuddyMixLearning />}
                      />
                      <Route
                        path="/learning/make-vocals-sit-in-mix"
                        element={<VocalsSitInMixLearning />}
                      />
                      <Route
                        path="/learning/how-to-keep-audio-levels-consistent"
                        element={<ConsistentAudioLevelsLearning />}
                      />
                      <Route
                        path="/learning/how-to-get-punchy-drums"
                        element={<PunchyDrumsLearning />}
                      />
                      <Route
                        path="/learning/how-to-tighten-low-end"
                        element={<TightenLowEndLearning />}
                      />
                      <Route
                        path="/learning/how-to-make-a-mix-louder-without-clipping"
                        element={<LouderMixWithoutClippingLearning />}
                      />
                      <Route
                        path="/learning/vocal-processing-chain"
                        element={<VocalProcessingChainLearning />}
                      />
                      <Route
                        path="/learning/best-reverb-settings-for-vocals"
                        element={<BestReverbSettingsForVocalsLearning />}
                      />

                      <Route path="/login" element={<Login />} />
                      <Route path="/subscribe" element={<Subscribe />} />
                      <Route path="/products/:sku" element={<ProductPage />} />
                      <Route
                        path="/bundles/:bundleURI"
                        element={<BundlePage />}
                      />
                      <Route
                        path="/reviews/:itemName"
                        element={<ProductReviewPage />}
                      />

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Route>
                </Routes>
              </ShoppingCartProvider>
            </PromotionsProvider>
          </StyleProvider>
        </UserContextProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;

// import './App.css';
// import React, { Suspense, lazy } from 'react';
// import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
// import {UserContextProvider} from './context/userContext/userContext'
// import PrivateRoutes from './routes/PrivateRoutes';
// import axios from 'axios'
// import Header from './components/Header/Header';
// import Footer from './components/Footer/Footer';
// import {Toaster} from 'react-hot-toast'
// import Home from './pages/Home/Home';
// import Register from './pages/Register/Register';
// import Terms from './pages/Terms/Terms';
// import Plugins from './pages/Plugins/Plugins'
// import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy'
// import { StyleProvider } from './context/styleContext/styleContext'
// import { ShoppingCartProvider } from './context/shoppingCartContext/shoppingCartContext';
// import Login from './pages/Login/Login';
// import About from './pages/About/About';
// import Contact from './pages/Contact/Contact';
// import FAQ from './pages/FAQ/FAQ';
// import Support from './pages/Support/Support';
// import Account from './pages/Account/Account';
// import CreateLicense from './components/CreateLicense/CreateLicense';
// import InfinityReverb from './pages/InfinityReverb/InfinityReverb';
// import PageAnalytics from './DataCollection/PageAnalytics';
// import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
// import CookieHandler from './components/CookieHandler/CookieHandler';
// import ResetPassword from './pages/ResetPassword/ResetPassword';
// import ProductsPage from './pages/Products/ProductsPage';
// import ProductPage from './pages/Product/ProductPage';
// import ProductReviewPage from './pages/Product/ProductReviewPage';
// import Admin from './pages/Admin/Admin';
// import AdminRoutes from './routes/AdminRoutes';
// import ShoppingCart from './pages/ShoppingCart/ShoppingCart';
// import MyGear from './pages/myGear/MyGear';
// //import CheckoutPage from './pages/Checkout/CheckoutPage';
// import GainDemo from './pages/GainDemo/GainDemo';
// import ReverbLearning from './pages/Learning/Reverb';
// import DelayLearning from './pages/Learning/Delay';
// import Learning from './pages/Learning/Learning';
// import CompressionLearning from './pages/Learning/Compression';
// import ChorusLearning from './pages/Learning/Chorus';
// import OverdriveLearning from './pages/Learning/Overdrive';
// import EQLearning from './pages/Learning/EQ';
// import SubConfirmPage from './pages/Subscription/SubConfirmPage';
// import DistortionLearning from './pages/Learning/Distortion';
// import AllReleases from './pages/AllReleases/AllReleases';
// import CompareDistOdFuzz from './pages/Learning/CompareDistOdFuzz';
// import ImageRendering from './pages/Admin/ImageRendering'
// import { HelmetProvider } from 'react-helmet-async';
// import ReturnPolicy from './pages/ReturnPolicy/ReturnPolicy';
// import SaturationLearning from './pages/Learning/Saturation';
// import LimitingLearning from './pages/Learning/Limiting';
// import DeEsserLearning from './pages/Learning/Deesser';
// import PhaserLearning from './pages/Learning/Phaser';
// import NoiseGateLearning from './pages/Learning/Noisegate';
// import FlangerLearning from './pages/Learning/Flanger';
// import StereoImagingLearning from './pages/Learning/StereoImaging';
// import TremoloLearning from './pages/Learning/Tremolo';
// import TransientShaperLearning from './pages/Learning/TransientShaper';
// import CompressorVsLimiterLearning from './pages/Learning/CompressorVsLimiter';
// import MasteringBasicsLearning from './pages/Learning/MasteringBasics';
// import SiteLayout from './components/SiteLayout/SiteLayout';
// import VibratoLearning from './pages/Learning/Vibrato';
// import GainStagingLearning from './pages/Learning/GainStaging';
// import BitcrusherLearning from './pages/Learning/Bitcrusher';
// import MultibandCompressionLearning from './pages/Learning/MultibandCompression';
// import BusProcessingLearning from './pages/Learning/BusProcessing';
// import ClippingVsLimitingLearning from './pages/Learning/ClippingVsLimiting';
// import ParallelProcessingLearning from './pages/Learning/ParallellProcessing';
// import AutomationInMixingLearning from './pages/Learning/AutomationInMixing';
// import SidechainCompressionLearning from './pages/Learning/SidechainCompression';
// import DynamicEQLearning from './pages/Learning/DynamicEQ';
// import CheckoutConfirmationPage from './pages/CheckoutConfirmation/CheckoutConfirmation';
// import CheckoutProcessingPage from './pages/CheckoutProcessing/CheckoutProcessing';
// import AdminPromotionsPage from './pages/Admin/AdminPromotions';
// import { PromotionsProvider } from './context/promotionsContext/promotionsContext';
// import PromotionBanner from './components/PromotionBanner/PromotionBanner';

// import EmailTesting from './pages/Admin/EmailTesting';
// import FixMuddyMixLearning from './pages/Learning/FixMuddyMix';
// import VocalsSitInMixLearning from './pages/Learning/MakeVocalsSitInMix';
// import ConsistentAudioLevelsLearning from './pages/Learning/KeepLevelsConsistent';
// import PunchyDrumsLearning from './pages/Learning/GetPunchyDrums';
// import TightenLowEndLearning from './pages/Learning/TightenLowEnd';
// import LouderMixWithoutClippingLearning from './pages/Learning/MixLouderWithoutClipping';
// import VocalProcessingChainLearning from './pages/Learning/VocalProcessingChain';
// import BestReverbSettingsForVocalsLearning from './pages/Learning/ReverbSettingsForVocals';
// import AdminProductsPage from './pages/Admin/AdminProductsPage';
// import AdminProductPage from './pages/Admin/AdminProductPage';
// import HarmonicSaturationLearning from './pages/Learning/HarmonicSaturation';
// import SaturationVsDistortionVsClippingLearning from './pages/Learning/SaturationVsDistortionVsClipping';
// import WhatIsATransientLearning from './pages/Learning/WhatIsATransient';
// import MakeVocalsSoundFullerLearning from './pages/Learning/HowToMakeVocalsSoundFuller';
// import SoftClippingVsHardClippingLearning from './pages/Learning/SoftClippingVsHardClipping';
// import UnsubscribeToMailingList from './pages/UnsubscribeToMailingList/UnsubscribeToMailingList';
// import MarketingEmailTesting from './pages/Admin/MarketingEmail';
// import AdminPluginUsagePage from './pages/Admin/AdminPluginUsagePage';
// import AdminContactsPage from './pages/Admin/AdminContactPage';
// import PressEmailPage from './pages/Admin/PressEmailPage';
// import RedeemPage from './pages/Redeem/Redeem';
// import AdminRedemptionCodes from './pages/Admin/RedemptionCodes';
// import AdminBundlesPage from './pages/Admin/AdminBundlesPage';
// import BundlePage from './pages/Bundle/BundlePage';
// import BundlesPage from './pages/Bundles/BundlesPage';


// const Subscribe = lazy(() => import('./pages/Subscribe/Subscribe'));
// const CheckoutPage = lazy(() => import('./pages/Checkout/CheckoutPage'));
// const ManageSubscription = lazy(() => import('./pages/Subscription/ManageSubscription'));
// if(process.env.NODE_ENV === 'development')
// {
//    axios.defaults.baseURL = 'http://localhost:8080';
//   //axios.defaults.baseURL = 'https://api.parishaudio.com/'
// }
// else
// {
//   axios.defaults.baseURL = 'https://api.parishaudio.com/'
// }


// axios.defaults.withCredentials = true;



// function App() {
//   return (
//     <HelmetProvider>
//     <BrowserRouter>
//     <UserContextProvider>
//     {/* <PageAnalytics endpoint={axios.defaults.baseURL + '/record-page-analytics'}> */}
//     <StyleProvider>
//     <PromotionsProvider>
//     <ShoppingCartProvider>
//     <CookieHandler />

//     <Toaster position='bottom-right' toastOptions={{
//     // Global default styles
//     duration:3000,
//     style: {
//       borderRadius: '8px',
//       background: '#1f2937', // Dark background
//       color: '#f9fafb',       // Light text
//       padding: '12px 16px',
//       fontSize: '1.3rem',
//     },
//     success: {
//       iconTheme: {
//         primary: '#10b981', // Tailwind Emerald-500
//         secondary: '#d1fae5',
//       },
//     },
//     error: {
//       iconTheme: {
//         primary: '#ef4444', // Tailwind Red-500
//         secondary: '#fee2e2',
//       },
//     },
//   }} />


//       <Routes>
//       <Route element={<SiteLayout />}>
      


//       <Route element= {<PrivateRoutes />}>

//         <Route path="account" element={<Account/>} />
//         <Route path="/my-gear" element={<MyGear />} />
//         <Route
//             path="/checkout"
//             element={
//               <Suspense fallback={<div>Loading checkout...</div>}>
//                 <CheckoutPage />
//               </Suspense>
//             }
//           />
//         <Route path="/manage-subscriptions" element = {<ManageSubscription />} />
//         <Route path="/subscription-confirmation" element = {<SubConfirmPage />} />
//         <Route path="/checkout-confirmation" element = {<CheckoutConfirmationPage />} />
//         <Route path="/checkout-processing" element = {<CheckoutProcessingPage />} />

//         {/* <Route path='/account' element={<Account />} />
//         <Route path='/my-gear' element={<MyGear />} />
//         <Route path= '/checkout' element = {<CheckoutPage />} />
//         <Route path= '/subscription-confirmation' element = {<SubConfirmPage />} /> */}

//       </Route>


//         <Route element={<AdminRoutes />}>
//           <Route path="/admin" element={<Admin />} />
//           <Route path="/admin/create-license" element={<CreateLicense />} />
//           <Route path="/gain-demo" element={<GainDemo />} />
//           <Route path="/admin/image-rendering" element = {<ImageRendering />} />
//           <Route path="admin/promotions" element = {<AdminPromotionsPage />} />
//           <Route path="/admin/products" element={<AdminProductsPage />} />
//           <Route path="/admin/products/:productId" element={<AdminProductPage />} />
//           <Route path="/admin/email-testing" element={<EmailTesting />} />
//           <Route path="/admin/marketing-email" element={<MarketingEmailTesting />} />
//           <Route path="/admin/press-email" element={<PressEmailPage />} />
//           <Route path="/admin/plugin-usage" element={<AdminPluginUsagePage/>} />
//           <Route path="/admin/contacts" element={<AdminContactsPage />} />
//           <Route path="/admin/redemption-codes" element={<AdminRedemptionCodes />} />
//           <Route path="/admin/bundles" element={<AdminBundlesPage />} />
//         </Route>



//         <Route path="/" element={<Home />} />
//         <Route path="/about" element={<About />} />
//         <Route path="/all-releases" element={<AllReleases />} />
//         <Route path="/contact" element={<Contact />} />
//         <Route path="/faq" element={<FAQ />} />
//         <Route path="/privacy-policy" element={<PrivacyPolicy />} />
//         <Route path="/support" element={<Support />} />
//         <Route path="/return-policy" element={<ReturnPolicy />} />
//         <Route path="/terms" element={<Terms />} />
//         <Route path="/plugins" element={<Plugins />} />
//         <Route path="/products" element={<ProductsPage />} />
//         <Route path="/bundles" element={<BundlesPage />} />
//         <Route path="/unsubscribe-to-mailing-list" element={<UnsubscribeToMailingList />} />
//         <Route path="/register" element={<Register />} />
//         <Route path="/shopping-cart" element={<ShoppingCart />} />
//         <Route path="/redeem" element={<RedeemPage />} />
//         <Route path="/forgot-password" element={<ForgotPassword />} />
//         <Route path='/reset-password/:token' element = {<ResetPassword/>} />
//         <Route path="/learning" element={<Learning />} />
//         <Route path="/learning/automation-in-mixing" element={<AutomationInMixingLearning />} />
//         <Route path="/learning/bitcrusher" element={<BitcrusherLearning />} />
//         <Route path="/learning/bus-processing" element={<BusProcessingLearning />} />
//         <Route path="/learning/chorus" element={<ChorusLearning />} />
//         <Route path="/learning/clipping-vs-limiting" element={<ClippingVsLimitingLearning />} />
//         <Route path="/learning/compare-distortion-overdrive-fuzz" element={<CompareDistOdFuzz />} />
//         <Route path="/learning/compression" element={<CompressionLearning />} />
//         <Route path="/learning/delay" element={<DelayLearning />} />
//         <Route path="/learning/distortion" element={<DistortionLearning />} /> 
//         <Route path="/learning/dynamic-eq" element={<DynamicEQLearning />} /> 
//         <Route path="/learning/equalization" element={<EQLearning />} />
//         <Route path="/learning/harmonic-saturation" element={<HarmonicSaturationLearning />} />
//         <Route path="/learning/saturation-vs-distortion-vs-clipping" element={<SaturationVsDistortionVsClippingLearning />} />
//         <Route path="/learning/what-is-a-transient" element={<WhatIsATransientLearning />} />
//         <Route path="/learning/how-to-make-vocals-sound-fuller" element={<MakeVocalsSoundFullerLearning />} />
//         <Route path="/learning/soft-clipping-vs-hard-clipping" element={<SoftClippingVsHardClippingLearning />} />
//         <Route path="/learning/limiting" element={<LimitingLearning />} />
//         <Route path="/learning/multi-band-compression" element={<MultibandCompressionLearning />} />
//         <Route path="/learning/overdrive" element={<OverdriveLearning />} />
//         <Route path="/learning/parallel-processing" element={<ParallelProcessingLearning />} />
//         <Route path="/learning/reverb" element={<ReverbLearning />} />
//         <Route path="/learning/saturation" element={<SaturationLearning />} />
//         <Route path="/learning/noise-gate" element={<NoiseGateLearning />} />
//         <Route path="/learning/de-esser" element={<DeEsserLearning />} />
//         <Route path="/learning/flanger" element={<FlangerLearning />} />
//         <Route path="/learning/gain-staging" element={<GainStagingLearning />} />
//         <Route path="/learning/stereo-imaging" element={<StereoImagingLearning />} />
//         <Route path="/learning/sidechain-compression" element={<SidechainCompressionLearning />} />
//         <Route path="/learning/tremolo" element={<TremoloLearning />} />
//         <Route path="/learning/vibrato" element={<VibratoLearning />} />
//         <Route path="/learning/phaser" element={<PhaserLearning />} />
//         <Route path="/learning/transient-shaper" element={<TransientShaperLearning />} />
//         <Route path="/learning/compressor-vs-limiter" element={<CompressorVsLimiterLearning />}/>
//         <Route path="/learning/mastering-basics" element={<MasteringBasicsLearning />}/>
//         <Route path="/learning/fix-muddy-mix" element={<FixMuddyMixLearning />} />
//         <Route path="/learning/make-vocals-sit-in-mix" element={<VocalsSitInMixLearning />} />
//         <Route path="/learning/how-to-keep-audio-levels-consistent" element={<ConsistentAudioLevelsLearning />} />
//         <Route path="/learning/how-to-get-punchy-drums" element={<PunchyDrumsLearning />} />
//         <Route path="/learning/how-to-tighten-low-end" element={<TightenLowEndLearning />} />
//         <Route path="/learning/how-to-make-a-mix-louder-without-clipping" element={<LouderMixWithoutClippingLearning />} />
//         <Route path="/learning/vocal-processing-chain" element={<VocalProcessingChainLearning />} />
//         <Route path="/learning/best-reverb-settings-for-vocals" element={<BestReverbSettingsForVocalsLearning />} />
//         <Route path="/login" element={<Login />} />
//         <Route path="/subscribe" element={<Subscribe />} />
//         <Route path="/products/:sku" element={<ProductPage />} />
//         <Route path="/bundles/:bundleURI" element={<BundlePage />} />
//         <Route path='/reviews/:itemName' element={<ProductReviewPage />} />
        

//         <Route path="*" element={<Navigate to="/" replace />} />
//         </Route>
//       </Routes>

//       </ShoppingCartProvider>
//       </PromotionsProvider>
//       </StyleProvider>
//       {/* </PageAnalytics> */}
//       </UserContextProvider>
//     </BrowserRouter>
//     </HelmetProvider>
//   );
// }

// export default App;

